/**
 * Offline Store — IndexedDB-backed cache for day pack data.
 *
 * Uses idb-keyval for simplicity (2KB, no schema).
 * Each data type is stored under a namespaced key with a timestamp.
 *
 * Data budget: ~500KB-2MB per day. IndexedDB limit on iPad Safari: ~1GB.
 */

import { get, set, del, keys } from 'idb-keyval'

// ── Types ──────────────────────────────────────────────────────────────

export interface CachedEntry<T> {
  data: T
  cachedAt: string // ISO timestamp
  email: string
}

// ── Keys ───────────────────────────────────────────────────────────────

const KEY = {
  missions: (email: string) => `offline:missions:${email}`,
  leads: (email: string) => `offline:leads:${email}`,
  appointments: (email: string) => `offline:appointments:${email}`,
  weekPlan: (email: string, bundesland: string) => `offline:weekplan:${email}:${bundesland}`,
  routes: (email: string) => `offline:routes:${email}`,
  syncTimestamp: (email: string) => `offline:lastSync:${email}`,
  // Durable day-state mirror. The `mission_day_state` table has NO SL write
  // endpoint, so the day lifecycle (open/home-return/close) is mirrored
  // offline-first into this IDB key. Survives reload. local-wins overlay on
  // top of any server read. (FR-FE-OFFLINE-FIRST-WRITE-STANDARD-2026-06-09)
  dayState: (email: string) => `offline:dayState:${email}`,
} as const

// ── Day-state mirror (offline-first) ────────────────────────────────────
//
// The day lifecycle is a FE-only mirror table with no Service-Layer write
// endpoint. Rather than hard-fail the rep's "Tag öffnen" when there is no
// SL write path (or no connection), the mirror is written durably to IDB
// FIRST and reflected immediately. The SL lifecycle POST
// (/start|/home-return|/finish) — queued separately when offline — remains
// the server source of truth. This is a sanctioned offline carve-out (no
// direct domain-table Supabase WRITE; the RLS-401 path is gone).

export interface LocalDayState {
  mission_id: string
  day_date: string
  user_email: string
  started_at: string | null
  home_return_at: string | null
  finished_at: string | null
  /** Local write timestamp — used to resolve local-vs-server overlay. */
  updated_at: string
}

function dayStateRowKey(missionId: string, dayDate: string): string {
  return `${missionId}::${dayDate}`
}

/** Read all durable local day-state rows for a user (keyed by mission+date). */
export async function getLocalDayStates(email: string): Promise<LocalDayState[]> {
  const map = await get<Record<string, LocalDayState>>(KEY.dayState(email))
  return map ? Object.values(map) : []
}

/**
 * Durably merge a day-state mutation into the local mirror. Only provided
 * fields overwrite; existing values are preserved (coalesce). Returns the
 * merged row so the caller can reflect it immediately. NEVER throws on
 * connection loss — persisting locally is the whole point.
 */
export async function upsertLocalDayState(patch: {
  missionId: string
  dayDate: string
  userEmail: string
  startedAt?: string | null
  homeReturnAt?: string | null
  finishedAt?: string | null
}): Promise<LocalDayState> {
  const map = (await get<Record<string, LocalDayState>>(KEY.dayState(patch.userEmail))) ?? {}
  const rowKey = dayStateRowKey(patch.missionId, patch.dayDate)
  const existing: LocalDayState = map[rowKey] ?? {
    mission_id: patch.missionId,
    day_date: patch.dayDate,
    user_email: patch.userEmail,
    started_at: null,
    home_return_at: null,
    finished_at: null,
    updated_at: new Date().toISOString(),
  }
  const merged: LocalDayState = {
    ...existing,
    started_at: patch.startedAt !== undefined ? patch.startedAt : existing.started_at,
    home_return_at: patch.homeReturnAt !== undefined ? patch.homeReturnAt : existing.home_return_at,
    finished_at: patch.finishedAt !== undefined ? patch.finishedAt : existing.finished_at,
    updated_at: new Date().toISOString(),
  }
  map[rowKey] = merged
  await set(KEY.dayState(patch.userEmail), map)
  return merged
}

// ── Read ───────────────────────────────────────────────────────────────

export async function getOfflineMissions<T>(email: string): Promise<CachedEntry<T> | undefined> {
  return get<CachedEntry<T>>(KEY.missions(email))
}

export async function getOfflineLeads<T>(email: string): Promise<CachedEntry<T> | undefined> {
  return get<CachedEntry<T>>(KEY.leads(email))
}

export async function getOfflineAppointments<T>(email: string): Promise<CachedEntry<T> | undefined> {
  return get<CachedEntry<T>>(KEY.appointments(email))
}

export async function getOfflineWeekPlan<T>(email: string, bundesland: string): Promise<CachedEntry<T> | undefined> {
  return get<CachedEntry<T>>(KEY.weekPlan(email, bundesland))
}

export async function getOfflineRoutes<T>(email: string): Promise<CachedEntry<T> | undefined> {
  return get<CachedEntry<T>>(KEY.routes(email))
}

export async function getLastSyncTimestamp(email: string): Promise<string | undefined> {
  return get<string>(KEY.syncTimestamp(email))
}

// ── Write ──────────────────────────────────────────────────────────────

export async function setOfflineMissions<T>(email: string, data: T): Promise<void> {
  const entry: CachedEntry<T> = { data, cachedAt: new Date().toISOString(), email }
  await set(KEY.missions(email), entry)
}

export async function setOfflineLeads<T>(email: string, data: T): Promise<void> {
  const entry: CachedEntry<T> = { data, cachedAt: new Date().toISOString(), email }
  await set(KEY.leads(email), entry)
}

export async function setOfflineAppointments<T>(email: string, data: T): Promise<void> {
  const entry: CachedEntry<T> = { data, cachedAt: new Date().toISOString(), email }
  await set(KEY.appointments(email), entry)
}

export async function setOfflineWeekPlan<T>(email: string, bundesland: string, data: T): Promise<void> {
  const entry: CachedEntry<T> = { data, cachedAt: new Date().toISOString(), email }
  await set(KEY.weekPlan(email, bundesland), entry)
}

export async function setOfflineRoutes<T>(email: string, data: T): Promise<void> {
  const entry: CachedEntry<T> = { data, cachedAt: new Date().toISOString(), email }
  await set(KEY.routes(email), entry)
}

export async function setLastSyncTimestamp(email: string): Promise<void> {
  await set(KEY.syncTimestamp(email), new Date().toISOString())
}

// ── Cleanup ────────────────────────────────────────────────────────────

/**
 * DSGVO Art. 17 — Right to erasure.
 * Wipes all IDB offline data for a specific user.
 * Called on logout, account deletion, and session timeout.
 */
export async function clearUserCache(email: string): Promise<number> {
  const allKeys = await keys()
  let cleared = 0

  for (const key of allKeys) {
    if (typeof key !== 'string') continue
    if (!key.startsWith('offline:')) continue
    if (key.includes(`:${email}`)) {
      await del(key)
      cleared++
    }
  }

  // Also clear the React Query IDB persister key
  try {
    await del('appic-react-query-cache')
    cleared++
  } catch {
    // best-effort
  }

  return cleared
}

/** Remove all offline data older than maxAgeMs (default 48h) */
export async function pruneStaleOfflineData(maxAgeMs = 48 * 60 * 60 * 1000): Promise<number> {
  const allKeys = await keys()
  const now = Date.now()
  let pruned = 0

  for (const key of allKeys) {
    if (typeof key !== 'string') continue
    if (!key.startsWith('offline:')) continue
    if (key.startsWith('offline:lastSync:')) continue // keep timestamps

    const entry = await get<CachedEntry<unknown>>(key)
    if (entry && entry.cachedAt) {
      const age = now - new Date(entry.cachedAt).getTime()
      if (age > maxAgeMs) {
        await del(key)
        pruned++
      }
    }
  }

  return pruned
}
